import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Shell } from "@/components/Shell";
import { DocRow } from "@/pages/Home";
import { BottomSheet } from "@/components/BottomSheet";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { DOC_META } from "@/lib/format";
import { FileText, Receipt, Recycle, Plus, FolderOpen, Eye, Trash2, Search, Lock } from "lucide-react";

const CreateBtn = ({ icon: Icon, label, color, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex-1 bg-surface rounded-[14px] border border-border py-3 flex flex-col items-center gap-1.5 hover:border-brandPrimary transition-colors active:scale-[0.97]">
    <div className="w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ background: color + "22" }}><Icon size={18} style={{ color }} /></div>
    <span className="font-heading font-bold text-xs text-onSurface flex items-center gap-0.5"><Plus size={11} />{label}</span>
  </button>
);

const FILTERS = [
  { key: "all", label: "All" },
  { key: "quote", label: "Quotes" },
  { key: "invoice", label: "Invoices" },
  { key: "waste_note", label: "Waste", wasteOnly: true },
];

export default function Documents() {
  const navigate = useNavigate();
  const { access } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [menuDoc, setMenuDoc] = useState(null);
  const [confirmDoc, setConfirmDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/documents").then(({ data }) => setDocs(data.documents)).finally(() => setLoading(false)); }, []);

  const create = (type) => {
    if (access && !access.can_create_documents) { navigate("/paywall"); return; }
    navigate(`/new/${type}`);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/documents/${confirmDoc.id}`);
      setDocs((ds) => ds.filter((x) => x.id !== confirmDoc.id));
      toast.success("Document deleted");
      setConfirmDoc(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); } finally { setBusy(false); }
  };

  const q = search.trim().toLowerCase();
  const filtered = docs.filter((d) => {
    if (filter !== "all" && d.type !== filter) return false;
    if (!q) return true;
    return [d.number, d.client?.name, d.data?.title, d.data?.description].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });
  const menuMeta = menuDoc ? (DOC_META[menuDoc.type] || {}) : {};
  const menuIsWaste = menuDoc?.type === "waste_note";

  return (
    <Shell showNav title="Documents" subtitle={`${docs.length} total`} right={
      <button data-testid="open-folders-btn" onClick={() => navigate("/folders")} className="flex items-center gap-1.5 bg-brandTertiary text-brandSecondary rounded-full pl-2.5 pr-3.5 h-9 font-heading font-bold text-sm active:scale-95 transition-transform">
        <FolderOpen size={16} /> Folders
      </button>
    }>
      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Create new</p>
      <div className="flex gap-2.5">
        <CreateBtn testid="doc-create-quote" icon={FileText} label="Quote" color="#E3592A" onClick={() => create("quote")} />
        <CreateBtn testid="doc-create-invoice" icon={Receipt} label="Invoice" color="#166534" onClick={() => create("invoice")} />
        {access?.can_waste_note && <CreateBtn testid="doc-create-waste" icon={Recycle} label="Waste note" color="#CA8A04" onClick={() => create("waste_note")} />}
      </div>

      <div className="relative mt-5">
        <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="doc-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by client, number or title…" className="w-full bg-surface border border-border rounded-full h-11 pl-10 pr-4 text-sm text-onSurface focus:outline-none focus:border-brandPrimary placeholder:text-muted-foreground" />
      </div>

      <div className="flex gap-2 mt-3 mb-3 overflow-x-auto no-scrollbar">
        {FILTERS.filter((f) => !f.wasteOnly || access?.can_waste_note).map((f) => (
          <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)} className={`shrink-0 px-3.5 h-8 rounded-full text-sm font-semibold transition-colors ${filter === f.key ? "bg-brandPrimary text-onBrand" : "bg-surface border border-border text-muted-foreground"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 bg-surfaceTertiary rounded-[12px] animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface rounded-[16px] border border-dashed border-borderStrong p-8 text-center mt-2">
          <div className="w-12 h-12 rounded-full bg-brandTertiary flex items-center justify-center mx-auto mb-3"><FileText size={22} className="text-brandSecondary" /></div>
          <p className="font-heading font-bold text-onSurface">{filter === "all" ? "No documents yet" : "Nothing here"}</p>
          <p className="text-sm text-muted-foreground mt-1">{filter === "all" ? "Create your first one above." : "Try a different filter."}</p>
        </div>
      ) : (
        <div className="space-y-2 pb-4 animate-fade-up">
          {filtered.map((d) => <DocRow key={d.id} d={d} onClick={() => navigate(`/doc/${d.id}`)} onMenu={setMenuDoc} />)}
        </div>
      )}

      {/* Row options menu */}
      <BottomSheet open={!!menuDoc} onClose={() => setMenuDoc(null)} title={menuDoc ? `${menuDoc.number} · ${menuMeta.label || "Document"}` : ""} testid="doc-menu-sheet">
        {menuDoc && (
          <div className="flex flex-col gap-2">
            <button data-testid="doc-menu-open" onClick={() => { const id = menuDoc.id; setMenuDoc(null); navigate(`/doc/${id}`); }} className="w-full bg-surface border border-border text-onSurface rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
              <Eye size={17} /> Open document
            </button>
            {menuIsWaste ? (
              <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5 py-1"><Lock size={12} /> Waste notes are kept 2+ years and can't be deleted</p>
            ) : (
              <button data-testid="doc-menu-delete" onClick={() => { const d = menuDoc; setMenuDoc(null); setConfirmDoc(d); }} className="w-full bg-surface border border-border text-error rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
                <Trash2 size={17} /> Delete document
              </button>
            )}
          </div>
        )}
      </BottomSheet>

      <ConfirmSheet
        open={!!confirmDoc}
        onClose={() => setConfirmDoc(null)}
        onConfirm={doDelete}
        busy={busy}
        title="Delete this document?"
        message={confirmDoc ? `${confirmDoc.number} · ${confirmDoc.client?.name || "No client"} will be permanently deleted. This can't be undone.` : ""}
        testid="doc-delete-confirm"
      />
    </Shell>
  );
}
